/**
 * Minimal logger for the player-web app.
 *
 * The player runs as an embedded kiosk — there is no DevTools access in
 * production and no backend logging pipeline. Rules:
 *  - In development: delegates to the browser console for visibility.
 *  - In production: all output is suppressed. Errors are forwarded to Sentry
 *    (configured in PlayerErrorBoundary) for remote visibility.
 *  - Warnings and errors also go to a small in-memory ring buffer that the remote
 *    `upload_logs` command sends to the dashboard (Onda 2).
 */
import * as Sentry from '@sentry/react'

const isDev = import.meta.env.DEV

/** Últimas entradas guardadas para o comando `upload_logs`; a API aceita no máximo 200. */
export const PLAYER_LOG_BUFFER_SIZE = 100
const MAX_MESSAGE_LENGTH = 500

export interface PlayerLogEntry {
  at: string
  level: 'warn' | 'error'
  message: string
}

const buffer: PlayerLogEntry[] = []

/**
 * Sai do device para o dashboard: sem query string de URL (URLs assinadas de mídia levam credencial), sem token
 * Bearer e sem sequências longas que parecem segredo.
 */
export function sanitizeLogText(text: string): string {
  return (
    text
      .replace(/(https?:\/\/[^\s?#"']+)[?#][^\s"']*/g, '$1')
      .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
      // Sem hífen na classe: UUIDs (ids de asset/playlist, úteis no diagnóstico) têm segmentos curtos e ficam.
      .replace(/[A-Za-z0-9_+/=]{32,}/g, '[redacted]')
      .slice(0, MAX_MESSAGE_LENGTH)
  )
}

function describe(context: unknown): string {
  if (context instanceof Error) return context.message
  if (context === undefined) return ''
  try {
    return typeof context === 'string' ? context : JSON.stringify(context)
  } catch {
    return String(context)
  }
}

function remember(level: PlayerLogEntry['level'], message: string, context?: unknown): void {
  const detail = describe(context)
  buffer.push({
    at: new Date().toISOString(),
    level,
    message: sanitizeLogText(detail ? `${message}: ${detail}` : message)
  })
  if (buffer.length > PLAYER_LOG_BUFFER_SIZE) buffer.splice(0, buffer.length - PLAYER_LOG_BUFFER_SIZE)
}

export const playerLogger = {
  debug: (message: string, ...context: unknown[]): void => {
    if (isDev) {
      console.debug(message, ...context)
    }
  },

  warn: (message: string, context?: unknown): void => {
    remember('warn', message, context)
    if (isDev) {
      console.warn(message, context)
    }
  },

  error: (message: string, error?: unknown): void => {
    remember('error', message, error)
    if (isDev) {
      console.error(message, error)
    } else if (error instanceof Error) {
      Sentry.captureException(error, { extra: { message } })
    } else {
      // Contexto estruturado (não-Error) vai como extra — antes ficava só no ring buffer local.
      Sentry.captureMessage(message, { level: 'error', extra: error === undefined ? undefined : { context: error } })
    }
  },

  /** Cópia das últimas entradas, mais antigas primeiro. */
  recent: (): PlayerLogEntry[] => [...buffer]
}
